import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type ComponentProps, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../lib/cn';

export const Tabs = TabsPrimitive.Root;

interface Indicator {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Lista de pestañas con un indicador que se desliza hasta la pestaña activa (en vez de saltar). Hasta
 * que se mide (primer render, tests sin layout) la pestaña activa se pinta sola con su fondo.
 */
export function TabsList({
  className,
  children,
  ref,
  ...props
}: ComponentProps<typeof TabsPrimitive.List>) {
  const own = useRef<HTMLDivElement | null>(null);
  const [ind, setInd] = useState<Indicator | null>(null);

  useLayoutEffect(() => {
    const list = own.current;
    if (!list) return;
    const measure = () => {
      const active = list.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
      if (!active || active.offsetWidth === 0) {
        setInd(null);
        return;
      }
      setInd({
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
      });
    };
    measure();
    const mo = new MutationObserver(measure);
    mo.observe(list, { attributes: true, subtree: true, attributeFilter: ['data-state'] });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(list);
    return () => {
      mo.disconnect();
      ro?.disconnect();
    };
  }, []);

  return (
    <TabsPrimitive.List
      ref={(node) => {
        own.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      data-indicator={ind ? 'ready' : undefined}
      className={cn(
        'group/tabs relative inline-flex h-10 max-w-full items-center gap-1 overflow-x-auto rounded-lg border border-border/60 bg-muted p-1 text-muted-foreground scrollbar-none',
        className,
      )}
      {...props}
    >
      {ind ? (
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-md bg-card shadow-card ring-1 ring-border/70 transition-[transform,width,height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{
            left: 0,
            top: 0,
            width: ind.width,
            height: ind.height,
            transform: `translate(${String(ind.left)}px, ${String(ind.top)}px)`,
          }}
        />
      ) : null}
      {children}
    </TabsPrimitive.List>
  );
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'relative z-10 inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium whitespace-nowrap transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs group-data-[indicator=ready]/tabs:data-[state=active]:bg-transparent group-data-[indicator=ready]/tabs:data-[state=active]:shadow-none [&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      className={cn(
        'mt-4 animate-rise outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      {...props}
    />
  );
}
